import {useLocation} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {getAvailableMainRoutes, getAvailableSigningRoutes} from '@utils/routes';
import {findMatchingRoute} from '@utils/routeMatching';
import {
    selectUser,
    selectFeatures,
    selectPermissions,
    selectHasUserPermissionOnRoot,
    selectGetIsDelegation
} from '@components/Login/userSlice';

/**
 * Hook to determine the current route configuration based on the current URL path.
 *
 * This hook combines both main application routes and signer UI routes,
 * filters them based on user permissions, features, and role, then finds
 * the matching route for the current location.
 *
 * @returns {Object|undefined} The matched route configuration object containing:
 *   - path: string - The route path pattern
 *   - childPath: string|undefined - The matched child route path (e.g. "2fa" for /account/2fa)
 *   - isPrivate: boolean - Whether the route requires authentication
 *   - isSignerRoute: boolean - Whether this is a signer UI route
 *   - component: React.Component - The component to render
 *   - ...other route-specific properties
 *
 *   Returns undefined if no matching route is found.
 *
 * @example
 * const currentRoute = useCurrentRoute();
 * if (currentRoute?.childPath === '2fa') {
 *   // On the 2FA setup page
 * }
 */
export function useCurrentRoute() {
    const location = useLocation();
    const user = useSelector(selectUser);
    const features = useSelector(selectFeatures);
    const permissions = useSelector(selectPermissions);
    const hasUserPermissionOnRoot = useSelector(selectHasUserPermissionOnRoot);
    const isDelegation = useSelector(selectGetIsDelegation);

    const allMainRoutes = getAvailableMainRoutes({
        user, features, permissions, hasUserPermissionOnRoot, isDelegation
    });
    const allSignerRoutes = getAvailableSigningRoutes({
        user, features, permissions, hasUserPermissionOnRoot, isDelegation
    });

    const allRoutes = [...allMainRoutes, ...allSignerRoutes];
    const matchedRoute = findMatchingRoute(location.pathname, allRoutes);

    if (!matchedRoute) {
        return undefined;
    }

    const childRoutes = matchedRoute.childrenRoutes || [];
    const remainingPath = location.pathname.slice(matchedRoute.path.length).replace(/^\//, '');
    const matchedChild = childRoutes.find(({path}) => remainingPath === path || remainingPath.startsWith(`${path}/`));

    return {
        ...matchedRoute,
        childPath: matchedChild?.path
    };
}
